/* Ring builder — the gemstone DETAIL step (step 2b).

   Every card in step 2 already linked here (`RB.pages.stone` = /pages/gemstone);
   the page itself was never built, so the whole grid pointed at a 404. This is
   that page.

   THE 360 IS THE STULLER MP4, NOT A FRAME SEQUENCE. stone_spin.py's docstring
   argues for frames and it is right on the merits — a 32-frame sequence seeks
   instantly and is 40-70x smaller than the 2-11 MB source clip. But frames have
   to be HOSTED: 4,535 stones x 32 frames is ~145,000 files on Shopify's CDN,
   which is not a thing you can ship. So v1 plays the clip Stuller already hosts:
     - `preload="none"` + the still as poster, so the page costs ONE image until
       the customer actually asks for the spin. A 6 MB autoload on every stone
       view would be indefensible.
     - PLAY is the primary gesture and DRAG is secondary, which is the opposite
       of a frame viewer. These clips are not faststart (verified: `mdat` sits in
       the first 128 KB and `moov` is at the tail), so sequential playback is
       smooth while a cold scrub has to wait for the tail range request. Playing
       first warms the buffer and makes the drag feel right.
   The seam is `mountSpin360` — swap its innards for frames the day a sprite
   sheet exists and nothing else on this page changes.

   TWO KINDS OF STONE render here and only one of them has most of the fields.
   Serialized stones (natural + lab diamond) carry carat, the 4Cs, a lab report
   and a turntable. Calibrated lab COLOR carries mm, species and nothing else —
   no carat, no cert, no video (verified in the shards). Every row below is
   therefore conditional; a spec table full of blank rows is worse than a short
   one. */
(function () {
  'use strict';
  var RB = window.RingBuilder;
  if (!RB) return;                    // deferred order: engine must parse first
  var el = RB.el, money = RB.money;

  var cap = function (s) {
    return String(s || '').toLowerCase().replace(/(^|[\s-])\w/g, function (m) {
      return m.toUpperCase();
    });
  };

  var mm = function (v) { return v == null ? null : v.toFixed(2) + ' mm'; };

  /* -------------------------------------------------------- durability ---- */
  // The shard carries the wearability LABEL; the long-form sentence lives in
  // RB_STONES_META.tiers keyed by tier. Invert on the label rather than shipping
  // the prose twice — gem_durability.py stays the single source.
  function wearCopy(label) {
    var tiers = (window.RB_STONES_META || {}).tiers || {};
    var hit = null;
    Object.keys(tiers).forEach(function (k) {
      if (tiers[k] && tiers[k][0] === label) hit = tiers[k];
    });
    return hit ? hit[2] : null;
  }

  /* ------------------------------------------------------------ 360 ------- */
  function mountSpin360(stage, url, poster, label) {
    var v = el('video', {
      class: 'rb-spin__v', preload: 'none', muted: 'muted', playsinline: 'playsinline',
      loop: 'loop', poster: poster || null, tabindex: '0',
      'aria-label': label || 'Gemstone 360 view — press play, or drag to rotate'
    });
    v.muted = true;                   // attribute alone does not satisfy every browser
    stage.appendChild(v);

    var hint = el('span', { class: 'rb-spin__hint', text: 'Drag to rotate' });
    var play = el('button', {
      class: 'rb-spin__play', type: 'button', 'aria-label': 'Play 360 view'
    }, [el('span', { class: 'rb-spin__tri', 'aria-hidden': 'true' })]);
    stage.appendChild(play);
    stage.appendChild(hint);

    var loaded = false, dragging = false, x0 = 0, t0 = 0;

    function load() {
      if (loaded) return Promise.resolve();
      loaded = true;
      stage.classList.add('is-loading');
      v.preload = 'auto';
      v.src = url;
      return new Promise(function (res) {
        var done = function () { stage.classList.remove('is-loading'); res(); };
        v.addEventListener('loadeddata', done, { once: true });
        v.addEventListener('error', function () {
          // never leave an empty plate: fall back to the still
          stage.classList.remove('is-loading');
          stage.classList.add('is-failed');
          res();
        }, { once: true });
        v.load();
      });
    }

    play.addEventListener('click', function () {
      load().then(function () {
        if (stage.classList.contains('is-failed')) return;
        stage.classList.add('is-live');
        if (v.paused) { v.play().catch(function () {}); } else { v.pause(); }
        play.setAttribute('aria-label', v.paused ? 'Play 360 view' : 'Pause 360 view');
      });
    });
    v.addEventListener('play', function () { stage.setAttribute('data-playing', 'true'); });
    v.addEventListener('pause', function () { stage.removeAttribute('data-playing'); });

    // DRAG TO SCRUB. One full rotation per 85% of the stage width.
    //
    // THE STONE FOLLOWS THE FINGER. Dragging right must turn the stone right,
    // which is what Blue Nile and every other 360 viewer does — a viewer that
    // moves opposite the pointer feels broken even when nobody can say why.
    // Stuller's turntable advances the stone LEFT as time increases, so the
    // drag has to run time BACKWARDS. This originally added, which inverted it,
    // and also disagreed with RB.mountSpin (the frame viewer) which subtracts.
    function seekBy(dx) {
      var d = v.duration;
      if (!d || !isFinite(d)) return;
      var t = t0 - (dx / (stage.clientWidth * 0.85)) * d;
      v.currentTime = ((t % d) + d) % d;
    }
    stage.addEventListener('pointerdown', function (e) {
      if (e.target === play || play.contains(e.target)) return;
      load().then(function () {
        if (stage.classList.contains('is-failed')) return;
        stage.classList.add('is-live');
        v.pause();
        dragging = true; x0 = e.clientX; t0 = v.currentTime || 0;
        stage.classList.add('is-drag');
        try { stage.setPointerCapture(e.pointerId); } catch (err) {}
      });
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      e.preventDefault();
      seekBy(e.clientX - x0);
    });
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      stage.addEventListener(ev, function (e) {
        dragging = false;
        stage.classList.remove('is-drag');
        try {
          if (e.pointerId != null && stage.hasPointerCapture(e.pointerId)) {
            stage.releasePointerCapture(e.pointerId);
          }
        } catch (err) {}
      });
    });

    // keyboard parity — the spin must be operable without a pointer
    v.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== ' ') return;
      e.preventDefault();
      load().then(function () {
        if (stage.classList.contains('is-failed')) return;
        stage.classList.add('is-live');
        if (e.key === ' ') {
          if (v.paused) v.play().catch(function () {}); else v.pause();
          return;
        }
        v.pause();
        var d = v.duration;
        if (!d || !isFinite(d)) return;
        var step = d / 32;                        // 32 notional frames, as stone_spin.py
        // matches the drag convention above: Right turns the stone right
        var t = v.currentTime + (e.key === 'ArrowRight' ? -step : step);
        v.currentTime = ((t % d) + d) % d;
      });
    });
    return v;
  }

  /* -------------------------------------------------------- spec table ---- */
  function specRows(s) {
    var rows = [];
    var add = function (k, val) { if (val != null && val !== '') rows.push([k, val]); };

    add('Shape', cap(s.shape));
    add('Type', (s.origin === 'lab' ? 'Lab-grown ' : 'Natural ') + (s.species || 'gemstone'));
    if (s.carat) add('Carat weight', s.carat.toFixed(2) + ' ct');
    if (s.l) {
      add('Measurements', s.w && Math.abs(s.w - s.l) > 0.005
        ? s.l.toFixed(2) + ' × ' + s.w.toFixed(2) + ' mm'
        : mm(s.l));
    }
    add('Color', s.color);
    add('Clarity', s.clarity);
    add('Cut', s.cut);
    // Mohs is a species property, so it rides in the shared meta rather than as
    // a column repeated 4,535 times. Absent until rb-stones-meta.js is rebuilt,
    // hence the guard — a missing row is correct, an "undefined" row is not.
    var mohs = ((window.RB_STONES_META || {}).mohs || {})[s.species];
    if (mohs) add('Hardness', mohs + ' on the Mohs scale');
    add('Wearability', s.wear);
    return rows;
  }

  /* -------------------------------------------------------------- mount --- */
  function mount(root, build, stone) {
    var body = root.querySelector('[data-rb-body]');
    if (!body) return;

    if (!stone) {
      body.innerHTML = '';
      body.appendChild(el('div', { class: 'rb-empty' }, [
        el('p', { text: 'We could not find that gemstone. It may have just sold — every ' +
                        'serialized stone here is one of one.' }),
        el('a', { class: 'btn btn-primary', style: 'margin-top:1.6rem',
          href: RB.href(build.origin === 'lab' ? RB.pages.stonesLab : RB.pages.stones,
                        build, { stone: null }),
          text: 'Back to all gemstones' })
      ]));
      return;
    }

    // ONE H1 per page (owner page-SEO rule). The section renders a static
    // heading; the real subject of this page is the stone, so retitle in place
    // rather than adding a second heading.
    var h1 = root.querySelector('h1');
    if (h1) h1.textContent = stone.title;
    try { document.title = stone.title + ' | Sapphire Row'; } catch (e) {}

    body.innerHTML = '';

    /* ---------------- media ---------------- */
    var stage = el('div', { class: 'rb-stage rb-spin' });
    if (stone.video) {
      mountSpin360(stage, stone.video, stone.img, stone.title + ' — 360 view');
      if (stone.img) {
        stage.insertBefore(el('img', { class: 'on rb-spin__still', src: stone.img,
          alt: stone.title, draggable: 'false' }), stage.firstChild);
      }
    } else if (stone.img) {
      stage.appendChild(el('img', { class: 'on', src: stone.img, alt: stone.title }));
    } else {
      // 2 serialized stones ship no media at Stuller at all — an edge case, not a bug
      stage.appendChild(el('span', { class: 'rb-noimg',
        text: 'Photography for this stone is on its way. Ask us and we will send it.' }));
    }
    var media = el('div', {}, [stage]);
    if (!stone.video) {
      media.appendChild(el('p', { class: 'rb-spin__none',
        text: stone.img ? 'This stone is calibrated stock, so it is photographed by type ' +
                          'rather than individually.' : '' }));
    }

    /* ---------------- info ---------------- */
    var info = el('div', { class: 'rb-detail__info' });

    info.appendChild(el('div', { class: 'rb-detail__price', text: money(stone.price) }));

    var fitLine = null;
    if (build.seat) {
      var ok = RB.fits(stone, build.seat);
      fitLine = el('p', { class: 'rb-detail__fit' + (ok ? ' is-ok' : ' is-bad'),
        text: ok ? 'Fits your ' + RB.seatLabel(build.seat) + ' setting'
                 : 'Does not fit your ' + RB.seatLabel(build.seat) +
                   ' setting — choose another stone, or change the setting size.' });
      info.appendChild(fitLine);
    }

    var tbl = el('dl', { class: 'rb-spec' });
    specRows(stone).forEach(function (r) {
      tbl.appendChild(el('dt', { text: r[0] }));
      tbl.appendChild(el('dd', { text: r[1] }));
    });
    info.appendChild(tbl);

    if (stone.lab && stone.cert) {
      info.appendChild(el('p', { class: 'rb-detail__cert' }, [
        el('span', { text: stone.lab + ' report ' + stone.cert + ' ' }),
        stone.certUrl ? el('a', { href: stone.certUrl, target: '_blank',
          rel: 'noopener nofollow', text: 'View the report' }) : null
      ]));
    }

    var longform = wearCopy(stone.wear);
    if (longform && stone.wear && stone.wear.indexOf('Excellent') < 0) {
      info.appendChild(el('div', { class: 'rb-note' }, [
        el('strong', { text: stone.wear + '. ' }),
        el('span', { text: longform })
      ]));
    }

    /* ---------------- CTA ---------------- */
    // Two entry orders are legitimate. Most customers arrive with a setting
    // already chosen; some land on a stone from search and have none. Sending
    // the second group to review would show them half a ring, so they go to
    // step 1 with the stone carried in the URL.
    var hasSetting = !!(build.style && build.seat);
    var next = hasSetting ? RB.pages.review : RB.pages.settings;
    // snap the seat to this stone — see RB.bestSeat. Without it the review page
    // inherits whatever seat was picked in step 1b, which may be a different
    // shape entirely.
    var seat = (build.style && RB.bestSeat(build.style, stone)) || null;
    var cta = el('a', {
      class: 'btn btn-primary rb-detail__cta',
      href: RB.href(next, build, { stone: stone.vid, shape: stone.shape,
                                   origin: stone.origin,
                                   seat: seat || build.seat || null }),
      text: hasSetting ? 'Choose this gemstone →' : 'Choose a setting for it →'
    });
    if (build.style && !seat) {
      // the style cannot hold this stone at all — say so rather than letting it
      // through to a cart line the bench would have to reject
      cta.removeAttribute('href');
      cta.setAttribute('aria-disabled', 'true');
      cta.classList.add('is-disabled');
      cta.textContent = 'Will not fit your setting';
    }
    if (!stone.available) {
      cta.removeAttribute('href');
      cta.setAttribute('aria-disabled', 'true');
      cta.classList.add('is-disabled');
      cta.textContent = 'Just sold';
    }
    info.appendChild(cta);
    info.appendChild(el('p', { class: 'rb-detail__back' }, [
      el('a', { href: RB.href(build.origin === 'lab' ? RB.pages.stonesLab : RB.pages.stones,
                              build, { stone: null }),
        text: '← Back to all gemstones' })
    ]));
    if (!hasSetting) {
      info.appendChild(el('p', { class: 'rb-detail__note',
        text: 'You have not picked a setting yet — we will take you there next and ' +
              'keep this stone with you.' }));
    }

    body.appendChild(media);
    body.appendChild(info);
  }

  RB.mountStoneDetail = mount;
})();
